/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { type ComponentType, lazy } from 'react'
import { DashSwitch } from '@retailer-platform/dashboard/routing'
import {
  ViewType,
  HideMarketplace,
  HideRetailerAgnosticStoreConfigs,
  HideNonMarketplaceRetailerAgnosticStoreConfigs,
  OnlyMarketplace,
  OnlyStorefront,
  OnlyStorefrontOrStorefrontPro,
} from '@retailer-platform/dashboard/gin-and-tonic'
import {
  contentPagesViewAccessControl,
  contentPagesDeletedAccessControl,
  pageBuilderModifyAccessControl,
  pageBuilderViewAccessControl,
  storeConfigurationDraftViewAccessControl,
  storefrontBannerLogoAccessControl,
  storefrontCustomImagesAccessControl,
  slotManagerViewAccessControl,
  sandboxAccessControl,
} from '../access-control/storefrontAccess.configuration'
import { DomainRoute } from '../utils/domain/routingComponents'
import {
  autosuggestModifyAccessControl,
  autosuggestViewAccessControl,
} from '../access-control/autosuggestAccess.configuration'
import {
  departmentNavigationAccessControl,
  navigationViewAccessControl,
} from '../access-control/navigationAccess.configuration'
import { creativeManagerV2AccessControl } from '../access-control/creativeManagerAccess.configuration'

const ContentPagesScopeWrapper = lazy(() =>
  import('../components/storefront-content-page-builder/ContentPagesScopeWrapper').then(m => ({
    default: m.ContentPagesScopeWrapper,
  }))
)

const StorefrontNavigationHeaderPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontNavigationHeaderPage" */
      '../pages/navigation/StorefrontNavigationHeaderPage'
    )
)
const StorefrontBrandSettingsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontBrandSettingsPage" */
      '../pages/brand-settings/StorefrontBrandSettingsPage'
    )
)
const StorefrontBrandSettingsPageV2 = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontBrandSettingsPage" */
      '../pages/brand-settings/StorefrontBrandSettingsPageV2'
    )
)
const StorefrontBrandEditPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontBrandSettingsPage" */
      '../pages/brand-settings/StorefrontBrandEditPage'
    )
)
const StorefrontCustomImagesPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontCustomImagesPage" */
      '../pages/brand-settings/StorefrontCustomImagesPage'
    )
)
const StorefrontCustomImagesPageV2 = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontCustomImagesPage" */
      '../pages/brand-settings/StorefrontCustomImagesPageV2'
    )
)
const StorefrontNavigationFooterPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontNavigationFooterPage" */
      '../pages/navigation/StorefrontNavigationFooterPage'
    )
)

const StorefrontPlacementCreateBuilderPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontPlacementBuilder" */
      '../pages/placement-builder/PlacementBuilderCreatePage'
    )
)

const StorefrontPlacementEditBuilderPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontPlacementBuilder" */
      '../pages/placement-builder/PlacementBuilderEditPage'
    )
)

const StorefrontContentPagesDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontContentPagesDashboardPage" */
      '../pages/content-pages-dashboard/StorefrontContentPagesDashboardPage'
    )
)
const StorefrontDeletedContentPagesDashboardPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontDeletedContentPagesDashboardPage" */
      '../pages/content-pages-dashboard/StorefrontDeletedContentPagesDashboardPage'
    )
)
const StorefrontContentPagesBuilder = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontContentPagesBuilder" */
      '../pages/content-pages-builder/StorefrontContentPagesBuilderPage'
    )
)
const StorefrontHomepageDashboard = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontHomepageDashboardPage" */
      '../pages/homepage-dashboard/StorefrontHomepageDashboardPage'
    )
)
const StorefrontHomepageBuilder = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontHomepageBuilderPage" */
      '../pages/homepage-builder/StorefrontHomepageBuilderPage'
    )
)
const StorefrontFlyoutBuilder = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontFlyoutBuilderPage" */
      '../pages/flyout-builder/StorefrontFlyoutBuilderPage'
    )
)
const StorefrontNavigationCollectionsPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontNavigationCollectionsPage" */
      '../pages/navigation/StorefrontNavigationCollectionsPage'
    )
)
const StorefrontAutosuggestLinksListPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontAutosuggestLinksListPage" */
      '../pages/autosuggest-links/StorefrontAutosuggestLinksListPage'
    )
)
const StorefrontAutosuggestLinksUpsertPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontAutosuggestLinksUpsertPage" */
      '../pages/autosuggest-links/StorefrontAutosuggestLinksUpsertPage'
    )
)
const StorefrontAutosuggestLinksViewPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontAutosuggestLinksViewPage" */
      '../pages/autosuggest-links/StorefrontAutosuggestLinksViewPage'
    )
)
const StorefrontItemAttributeIconsPage = lazy(
  () => import('../pages/item-attribute-icons/StorefrontItemAttributeIconsPage')
)
const SlotsManagerPage = lazy(() => import('../pages/slots-manager/SlotsManagerPage'))
const StorefrontBannerLogoPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontBannerLogoPage" */
      '../pages/brand-settings/StorefrontBannerLogoPage'
    )
)
const StorefrontCreativeManagerRecordViewPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontCreativeManagerRecordView" */
      '../pages/creative-manager/CreativeManagerRecordView'
    )
)
const StorefrontSandboxPage = lazy(
  () =>
    import(
      /* webpackChunkName: "StorefrontSandboxPage" */
      '../pages/sandbox/StorefrontSandboxPage'
    )
)
// @hygen:inject page-import

export const storefrontRouter = (
  <DomainRoute route="storefront" tool="custom-homepage-dashboard-tool">
    <DashSwitch>
      <DomainRoute
        exact
        route="storefront-brand-settings"
        tool="storefront-branding-dashboard-tool"
        component={StorefrontBrandSettingsPage as ComponentType<any>}
        accessControl={storeConfigurationDraftViewAccessControl}
        scopePicker={{
          titles: ['storefrontDomain.brand-settings.header'],
          selectionType: ViewType.STORE_CONFIG,
          storeConfigOperationIntent: 'modify',
          storeConfigFilter: HideRetailerAgnosticStoreConfigs,
        }}
      />
      <DomainRoute
        exact
        route="storefront-brand-settings-storefront"
        tool="storefront-branding-dashboard-tool"
        component={StorefrontBrandSettingsPage as ComponentType<any>}
        accessControl={storeConfigurationDraftViewAccessControl}
        scopePicker={{
          titles: ['storefrontDomain.brand-settings.header'],
          selectionType: ViewType.STORE_CONFIG,
          storeConfigOperationIntent: 'modify',
          storeConfigFilter: sites => OnlyStorefront(HideRetailerAgnosticStoreConfigs(sites)),
        }}
      />
      <DomainRoute
        exact
        route="storefront-brand-settings-storefront-pro"
        tool="storefront-branding-dashboard-tool"
        component={StorefrontBrandSettingsPage as ComponentType<any>}
        accessControl={storeConfigurationDraftViewAccessControl}
        scopePicker={{
          titles: ['storefrontDomain.brand-settings.header'],
          selectionType: ViewType.STORE_CONFIG,
          storeConfigOperationIntent: 'modify',
          storeConfigFilter: sites =>
            OnlyStorefrontOrStorefrontPro(HideRetailerAgnosticStoreConfigs(sites)),
        }}
      />
      <DomainRoute
        exact
        route="storefront-brand-settings-v2"
        tool="storefront-branding-dashboard-tool"
        component={StorefrontBrandSettingsPageV2 as ComponentType<any>}
        accessControl={storeConfigurationDraftViewAccessControl}
        scopePicker={{
          titles: ['storefrontDomain.brand-settings.header'],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'modify',
          storeConfigFilter: HideRetailerAgnosticStoreConfigs,
        }}
      />
      <DomainRoute
        exact
        route="storefront-brand-settings-edit"
        tool="storefront-branding-dashboard-tool"
        component={StorefrontBrandEditPage as ComponentType<any>}
        accessControl={storeConfigurationDraftViewAccessControl}
      />
      <DomainRoute
        exact
        route="storefront-custom-images"
        tool="custom-images-dashboard-tool"
        component={StorefrontCustomImagesPage as ComponentType<any>}
        accessControl={storefrontCustomImagesAccessControl}
        scopePicker={{
          titles: ['storefrontDomain.brand-settings.header'],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'modify',
          storeConfigFilter: HideRetailerAgnosticStoreConfigs,
        }}
      />
      <DomainRoute
        exact
        route="storefront-custom-images-v2"
        tool="custom-images-dashboard-tool"
        component={StorefrontCustomImagesPageV2 as ComponentType<any>}
        accessControl={storefrontCustomImagesAccessControl}
        scopePicker={{
          titles: ['storefrontDomain.brand-settings.header'],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'modify',
          storeConfigFilter: HideRetailerAgnosticStoreConfigs,
        }}
      />
      <DomainRoute
        exact
        route="storefront-navigation-header"
        tool="navigation-legacy-dashboard-tool"
        component={StorefrontNavigationHeaderPage as ComponentType<any>}
        accessControl={navigationViewAccessControl}
        scopePicker={{
          titles: ['storefrontDomain.navigation.navigation.title'],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'modify',
          storeConfigFilter: HideRetailerAgnosticStoreConfigs,
        }}
      />
      <DomainRoute
        exact
        route="storefront-navigation-header-storefront-pro"
        tool="navigation-legacy-dashboard-tool"
        component={StorefrontNavigationHeaderPage as ComponentType<any>}
        accessControl={navigationViewAccessControl}
        scopePicker={{
          titles: ['storefrontDomain.navigation.navigation.title'],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'modify',
          storeConfigFilter: sites =>
            OnlyStorefrontOrStorefrontPro(HideRetailerAgnosticStoreConfigs(sites)),
        }}
      />
      <DomainRoute
        exact
        route="storefront-navigation-footer"
        tool="navigation-legacy-dashboard-tool"
        component={StorefrontNavigationFooterPage as ComponentType<any>}
        accessControl={navigationViewAccessControl}
        scopePicker={{
          titles: [], // intentionally left empty. No header
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'view',
          storeConfigFilter: HideRetailerAgnosticStoreConfigs,
        }}
      />
      <DomainRoute
        exact
        route="storefront-navigation-collections"
        tool="navigation-dashboard-tool"
        component={StorefrontNavigationCollectionsPage as ComponentType<any>}
        accessControl={departmentNavigationAccessControl}
        scopePicker={{
          titles: [], // intentionally left empty. No header
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'view',
          storeConfigFilter: HideNonMarketplaceRetailerAgnosticStoreConfigs,
        }}
      />
      <DomainRoute
        exact
        route="storefront-marketplace-navigation-collections"
        tool="navigation-dashboard-tool"
        component={StorefrontNavigationCollectionsPage as ComponentType<any>}
        accessControl={departmentNavigationAccessControl}
        scopePicker={{
          titles: [],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'view',
          storeConfigFilter: OnlyMarketplace,
        }}
      />
      <DomainRoute
        exact
        route="storefront-navigation-collections-storefront"
        tool="navigation-dashboard-tool"
        component={StorefrontNavigationCollectionsPage as ComponentType<any>}
        accessControl={departmentNavigationAccessControl}
        scopePicker={{
          titles: [],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'view',
          storeConfigFilter: sites => OnlyStorefront(HideMarketplace(sites)),
        }}
      />
      <DomainRoute
        exact
        route="storefront-navigation-collections-storefront-pro"
        tool="navigation-dashboard-tool"
        component={StorefrontNavigationCollectionsPage as ComponentType<any>}
        accessControl={departmentNavigationAccessControl}
        scopePicker={{
          titles: [],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'view',
          storeConfigFilter: sites => OnlyStorefrontOrStorefrontPro(HideMarketplace(sites)),
        }}
      />
      <DomainRoute
        exact
        route="storefront-placement-builder-create-with-type"
        tool="creatives-dashboard-tool"
        component={StorefrontPlacementCreateBuilderPage as ComponentType<any>}
        accessControl={pageBuilderModifyAccessControl}
      />
      <DomainRoute
        exact
        route="storefront-placement-builder-edit"
        tool="creatives-dashboard-tool"
        component={StorefrontPlacementEditBuilderPage as ComponentType<any>}
        accessControl={pageBuilderModifyAccessControl}
      />
      <DomainRoute
        exact
        route="storefront-content-pages-dashboard"
        tool="content-pages-dashboard-tool"
        component={StorefrontContentPagesDashboardPage as ComponentType<any>}
        accessControl={contentPagesViewAccessControl}
        scopePickerComponent={ContentPagesScopeWrapper as ComponentType<any>}
      />
      <DomainRoute
        exact
        route="storefront-content-pages-dashboard-storefront-pro"
        tool="content-pages-dashboard-tool"
        component={StorefrontContentPagesDashboardPage as ComponentType<any>}
        accessControl={contentPagesViewAccessControl}
        scopePicker={{
          titles: ['storefrontDomain.contentPages.dashboard.pageTitle'],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'view',
          storeConfigFilter: sites =>
            OnlyStorefrontOrStorefrontPro(HideRetailerAgnosticStoreConfigs(sites)),
        }}
      />
      <DomainRoute
        exact
        route="storefront-content-pages-deleted"
        tool="content-pages-dashboard-tool"
        component={StorefrontDeletedContentPagesDashboardPage as ComponentType<any>}
        accessControl={contentPagesDeletedAccessControl}
        scopePickerComponent={ContentPagesScopeWrapper as ComponentType<any>}
      />
      <DomainRoute
        exact
        route="storefront-content-pages-deleted-storefront-pro"
        tool="content-pages-dashboard-tool"
        component={StorefrontDeletedContentPagesDashboardPage as ComponentType<any>}
        accessControl={contentPagesDeletedAccessControl}
        scopePicker={{
          titles: ['storefrontDomain.contentPages.dashboard.pageTitle'],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'view',
          storeConfigFilter: sites =>
            OnlyStorefrontOrStorefrontPro(HideRetailerAgnosticStoreConfigs(sites)),
        }}
      />
      <DomainRoute
        exact
        route="storefront-content-pages-create"
        tool="content-pages-dashboard-tool"
        component={StorefrontContentPagesBuilder as ComponentType<any>}
      />
      <DomainRoute
        exact
        route="storefront-content-pages-edit"
        tool="content-pages-dashboard-tool"
        component={StorefrontContentPagesBuilder as ComponentType<any>}
      />
      <DomainRoute
        exact
        route="storefront-content-pages-edit-version"
        tool="content-pages-dashboard-tool"
        component={StorefrontContentPagesBuilder as ComponentType<any>}
      />
      <DomainRoute
        exact
        route="storefront-flyout-pages-create"
        tool="content-pages-dashboard-tool"
        component={StorefrontFlyoutBuilder as ComponentType<any>}
        accessControl={pageBuilderModifyAccessControl}
      />
      <DomainRoute
        exact
        route="storefront-flyout-pages-edit"
        tool="content-pages-dashboard-tool"
        component={StorefrontFlyoutBuilder as ComponentType<any>}
        accessControl={pageBuilderModifyAccessControl}
      />
      <DomainRoute
        exact
        route="storefront-homepage-dashboard"
        tool="custom-homepage-dashboard-tool"
        component={StorefrontHomepageDashboard as ComponentType<any>}
        accessControl={pageBuilderViewAccessControl}
        scopePicker={{
          titles: [],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'modify',
          storeConfigFilter: HideRetailerAgnosticStoreConfigs,
        }}
      />
      <DomainRoute
        exact
        route="storefront-homepage-edit"
        tool="custom-homepage-dashboard-tool"
        component={StorefrontHomepageBuilder as ComponentType<any>}
        accessControl={pageBuilderViewAccessControl}
      />
      <DomainRoute
        exact
        route="storefront-autosuggest-links-list"
        tool="keyword-redirects-dashboard-tool"
        component={StorefrontAutosuggestLinksListPage as ComponentType<any>}
        accessControl={autosuggestViewAccessControl}
        scopePicker={{
          // titles: [],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'view',
          storeConfigFilter: HideRetailerAgnosticStoreConfigs,
        }}
      />
      <DomainRoute
        exact
        route="storefront-autosuggest-links-list-storefront"
        tool="keyword-redirects-dashboard-tool"
        component={StorefrontAutosuggestLinksListPage as ComponentType<any>}
        accessControl={autosuggestViewAccessControl}
        scopePicker={{
          // titles: [],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'view',
          storeConfigFilter: sites => OnlyStorefront(HideRetailerAgnosticStoreConfigs(sites)),
        }}
      />
      <DomainRoute
        exact
        route="storefront-autosuggest-links-list-storefront-pro"
        tool="keyword-redirects-dashboard-tool"
        component={StorefrontAutosuggestLinksListPage as ComponentType<any>}
        accessControl={autosuggestViewAccessControl}
        scopePicker={{
          // titles: [],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'view',
          storeConfigFilter: sites =>
            OnlyStorefrontOrStorefrontPro(HideRetailerAgnosticStoreConfigs(sites)),
        }}
      />
      <DomainRoute
        exact
        route="storefront-autosuggest-links-upsert"
        tool="keyword-redirects-dashboard-tool"
        component={StorefrontAutosuggestLinksUpsertPage as ComponentType<any>}
        accessControl={autosuggestModifyAccessControl}
        scopePicker={{
          titles: [], // left empty on purpose. We cannot accurately decide between "edit" or "new" title
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'modify',
          // Keyword Redirects is Storefront/SFP only — v3 nav attaches it under
          // Storefront and StorefrontPro L1s, not Marketplace. Matches the list
          // route filter so the picker stays consistent across list and upsert.
          storeConfigFilter: HideRetailerAgnosticStoreConfigs,
        }}
      />
      <DomainRoute
        exact
        route="storefront-autosuggest-links-view"
        tool="keyword-redirects-dashboard-tool"
        component={StorefrontAutosuggestLinksViewPage as ComponentType<any>}
        accessControl={autosuggestViewAccessControl}
      />
      <DomainRoute
        exact
        route="storefront-item-attribute-icons"
        tool="item-attribute-icons-dashboard-tool"
        component={StorefrontItemAttributeIconsPage as ComponentType<any>}
        scopePicker={{
          titles: [],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'modify',
          storeConfigFilter: HideRetailerAgnosticStoreConfigs,
        }}
      />
      <DomainRoute
        exact
        route="storefront-slots-manager"
        tool="slot-manager-dashboard-tool"
        component={SlotsManagerPage as ComponentType<any>}
        accessControl={slotManagerViewAccessControl}
        scopePicker={{
          titles: [],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'view',
          // Slot Manager is attached under the Storefront Pro L1. A combined
          // SF+SFP retailer reaches all storefront tools through the SFP L1, so
          // the picker allows both Storefront and Storefront Pro store configs.
          storeConfigFilter: OnlyStorefrontOrStorefrontPro,
        }}
      />
      <DomainRoute
        exact
        route="storefront-slots-manager-detail"
        tool="slot-manager-dashboard-tool"
        component={SlotsManagerPage as ComponentType<any>}
        accessControl={slotManagerViewAccessControl}
      />
      <DomainRoute
        exact
        route="storefront-banner-logo"
        tool="storefront-branding-dashboard-tool"
        component={StorefrontBannerLogoPage as ComponentType<any>}
        accessControl={storefrontBannerLogoAccessControl}
        scopePicker={{
          titles: ['storefrontDomain.brand-settings.header'],
          selectionType: ViewType.RETAILER_AND_STORE_CONFIG,
          storeConfigOperationIntent: 'modify',
          storeConfigFilter: HideRetailerAgnosticStoreConfigs,
        }}
      />
      <DomainRoute
        exact
        route="storefront-creative-view"
        tool="creatives-dashboard-tool"
        component={StorefrontCreativeManagerRecordViewPage as ComponentType<any>}
        accessControl={creativeManagerV2AccessControl}
      />
      {/* @hygen:inject router-route
        injection point for routes, please do not delete */}
    </DashSwitch>
  </DomainRoute>
)

// Retailer-scoped sibling of storefrontRouter. Sandbox lives here (rather than
// under URL_STOREFRONT_SITE_SCOPED) so opening the page does NOT force a
// banner pick — banner is chosen inside the provision modal, and the table
// groups/filters by banner client-side (mirrors the Flyers list pattern).
//
// `scopePicker` must be set (even though we don't want a picker UI) because
// RetailerScopeWrapper is what mounts the HeaderTitlePortalTarget that
// HeaderPortal renders into — without it, the page header wouldn't appear.
// `hiddenScopePicker: true` suppresses the picker chrome on the left while
// keeping the wrapper (and thus the header target) mounted.
export const storefrontRetailerScopedRouter = (
  <DomainRoute route="storefront-retailer-scoped">
    <DashSwitch>
      <DomainRoute
        exact
        route="storefront-sandbox"
        component={StorefrontSandboxPage as ComponentType<any>}
        accessControl={sandboxAccessControl}
        scopePicker={{
          hiddenScopePicker: true,
          titles: ['storefrontDomain.sandbox.pageTitle'],
        }}
      />
    </DashSwitch>
  </DomainRoute>
)
